import React, { useState, useEffect } from 'react';
import { Wrench, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { apiClient } from '../../utils/apiClient';
import { toast } from 'sonner';

export default function MaintenanceToggle() {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'enable' oder 'disable'

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await apiClient.get('/api/system/status');
      const data = await res.json();
      setMaintenanceMode(data.maintenance_mode || false);
    } catch (error) {
      console.error('Fehler beim Laden des Wartungsmodus-Status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleClick = (enable) => {
    setPendingAction(enable ? 'enable' : 'disable');
    setConfirmText('');
    setShowDialog(true);
  };

  const handleConfirm = async () => {
    if (confirmText.trim().toUpperCase() !== 'WARTUNG') {
      toast.error('Bitte "WARTUNG" eingeben');
      return;
    }

    setActionLoading(true);

    try {
      const res = await apiClient.post('/api/admin/maintenance/toggle', {
        enable: pendingAction === 'enable',
        confirmation_text: confirmText
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Fehler beim Umschalten');
      }

      const data = await res.json();
      setMaintenanceMode(data.maintenance_mode);
      
      toast.success(
        data.maintenance_mode 
          ? '🔧 Wartungsmodus aktiviert - Seite ist jetzt gesperrt' 
          : '✅ Wartungsmodus deaktiviert - Seite ist wieder verfügbar'
      );
      
      setShowDialog(false);
      setConfirmText('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Wartungsmodus
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-zinc-400">Lädt...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="w-5 h-5" />
            Wartungsmodus
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          
          {/* Status */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">
                Status: {maintenanceMode ? (
                  <span className="text-orange-500">An</span>
                ) : (
                  <span className="text-emerald-500">Aus</span>
                )}
              </p>
              <p className="text-sm text-zinc-500">
                {maintenanceMode 
                  ? 'Seite ist für alle Nutzer gesperrt' 
                  : 'Seite ist normal erreichbar'}
              </p>
            </div>
          </div>

          {/* Warnung wenn aktiv */}
          {maintenanceMode && (
            <Alert className="bg-orange-950/30 border-orange-500/50">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                ⚠️ Die Seite ist aktuell für alle Nutzer gesperrt!
              </AlertDescription>
            </Alert>
          )}

          {/* Toggle Button */}
          {maintenanceMode ? (
            <Button
              onClick={() => handleToggleClick(false)}
              variant="outline"
              className="w-full border-emerald-600 text-emerald-500 hover:bg-emerald-950"
            >
              Wartungsmodus beenden
            </Button>
          ) : (
            <Button
              onClick={() => handleToggleClick(true)}
              variant="outline"
              className="w-full border-orange-600 text-orange-500 hover:bg-orange-950"
            >
              Wartungsmodus aktivieren
            </Button>
          )}
          
        </CardContent>
      </Card>

      {/* Bestätigungs-Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pendingAction === 'enable' ? (
                <>
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Wartungsmodus aktivieren?
                </>
              ) : (
                <>
                  <Wrench className="w-5 h-5 text-emerald-500" />
                  Wartungsmodus beenden?
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {pendingAction === 'enable' ? (
                <div className="space-y-3">
                  <Alert className="bg-orange-950/30 border-orange-500/50">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      ⚠️ ACHTUNG: Die gesamte Seite wird für alle Nutzer gesperrt!
                    </AlertDescription>
                  </Alert>
                  <p className="text-zinc-400">
                    Nur du (Admin) kannst weiterhin auf das System zugreifen.
                  </p>
                </div>
              ) : (
                <p className="text-zinc-400">
                  Die Seite wird wieder für alle Nutzer freigeschaltet.
                </p>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">
                Gib <strong>WARTUNG</strong> ein, um zu bestätigen:
              </label>
              <Input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="WARTUNG"
                className="bg-zinc-800 border-zinc-700 text-white"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleConfirm}
                disabled={actionLoading || confirmText.trim().toUpperCase() !== 'WARTUNG'}
                className={pendingAction === 'enable' 
                  ? 'flex-1 bg-orange-600 hover:bg-orange-700' 
                  : 'flex-1 bg-emerald-600 hover:bg-emerald-700'
                }
              >
                {actionLoading ? 'Speichere...' : 'Bestätigen'}
              </Button>
              
              <Button
                onClick={() => {
                  setShowDialog(false);
                  setConfirmText('');
                }}
                variant="outline"
                className="flex-1 border-zinc-700"
                disabled={actionLoading}
              >
                Abbrechen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
